import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_FILTERS = ["pending", "approved", "rejected", "all"];

// All requests are credentialed — admin auth rides the HttpOnly `noor_admin`
// cookie set server-side by POST /api/admin/session.
const REQ = { withCredentials: true };

const PILL_STYLES = {
  pending:  { background: "rgba(201,164,122,0.10)", color: "var(--accent-rose)", border: "1px solid rgba(201,164,122,0.32)" },
  approved: { background: "rgba(122,201,140,0.10)", color: "#9BD6A2",             border: "1px solid rgba(155,214,162,0.32)" },
  rejected: { background: "rgba(226,118,118,0.08)", color: "#E27676",             border: "1px solid rgba(226,118,118,0.32)" },
};

const ACTION_BTN_STYLE = { fontSize: "0.7rem", padding: "0.7rem 1.3rem" };

// ---------- API helpers ----------
async function adminLogin(token) {
  await axios.post(`${API}/admin/session`, { token: token.trim() }, REQ);
}
async function adminLogout() {
  await axios.delete(`${API}/admin/session`, REQ);
}
async function adminSessionStatus() {
  const res = await axios.get(`${API}/admin/session`, REQ);
  return res.data?.authenticated === true;
}
async function fetchApplications(status) {
  const qs = status && status !== "all" ? `?status=${status}` : "";
  const res = await axios.get(`${API}/admin/applications${qs}`, REQ);
  return res.data;
}
async function fetchEmailLog() {
  const res = await axios.get(`${API}/admin/email-log?limit=20`, REQ);
  return res.data;
}
async function approveApplication(id) {
  const res = await axios.post(`${API}/admin/applications/${id}/approve`, {}, REQ);
  return res.data;
}
async function rejectApplication(id) {
  const res = await axios.post(`${API}/admin/applications/${id}/reject`, {}, REQ);
  return res.data;
}

// ---------- Token gate ----------
function TokenGate({ onAuthed }) {
  const [token, setToken] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await adminLogin(token);
      onAuthed();
    } catch (err) {
      setError(err?.response?.status === 401 ? "Token rejected." : "Could not reach the admin endpoint.");
      setBusy(false);
    }
  }, [token, onAuthed]);

  return (
    <div className="min-h-screen flex items-start justify-center px-6 pt-32 md:pt-40" data-testid="admin-token-gate">
      <form onSubmit={submit} className="liquid-glass p-10 max-w-md w-full rounded-sm">
        <p className="overline mb-4" style={{ color: "var(--accent-rose)" }}>House Admin</p>
        <h1 className="font-display text-3xl mb-3" style={{ color: "var(--noor-bone)" }}>
          The keeper&rsquo;s door.
        </h1>
        <p className="text-sm mb-8 font-light" style={{ color: "var(--text-secondary)" }}>
          Paste your admin token to review applications and dispatch invitations.
        </p>
        <input
          data-testid="admin-token-input"
          type="password"
          autoComplete="current-password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="X-Admin-Token"
          className="input-line w-full mb-6 font-mono"
        />
        {error && (
          <p data-testid="admin-token-error" className="text-xs mb-4" style={{ color: "#E27676" }}>{error}</p>
        )}
        <button data-testid="admin-token-submit" type="submit" disabled={busy} className="btn-solid w-full justify-center">
          {busy ? "Verifying…" : "Enter"}
        </button>
      </form>
    </div>
  );
}

// ---------- Small presentational pieces ----------
function StatusPill({ status }) {
  const style = PILL_STYLES[status] || PILL_STYLES.pending;
  return (
    <span
      className="inline-block font-mono text-[9px] uppercase px-2 py-1 rounded-sm"
      style={{ ...style, letterSpacing: "0.24em" }}
      data-testid={`admin-status-${status}`}
    >
      {status}
    </span>
  );
}

function ApplicationActions({ appId, busy, onApprove, onReject }) {
  return (
    <div className="flex gap-3 flex-wrap">
      <button
        data-testid={`admin-approve-${appId}`}
        onClick={onApprove}
        disabled={busy !== null}
        className="btn-solid"
        style={ACTION_BTN_STYLE}
      >
        {busy === "approve" ? "Approving…" : "Approve · mint code"}
      </button>
      <button
        data-testid={`admin-reject-${appId}`}
        onClick={onReject}
        disabled={busy !== null}
        className="btn-ghost"
        style={ACTION_BTN_STYLE}
      >
        {busy === "reject" ? "Rejecting…" : "Reject"}
      </button>
    </div>
  );
}

// Hook hosting the per-row mutation state — keeps ApplicationRow declarative.
function useRowActions({ app, onChanged, onApproved }) {
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const onApprove = useCallback(async () => {
    setBusy("approve"); setErr(null);
    try {
      const r = await approveApplication(app.id);
      setResult(r);
      if (onApproved) onApproved({ ...r, full_name: app.full_name, email: app.email });
      onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Approval failed.");
    } finally { setBusy(null); }
  }, [app.id, app.full_name, app.email, onChanged, onApproved]);

  const onReject = useCallback(async () => {
    setBusy("reject"); setErr(null);
    try {
      await rejectApplication(app.id);
      onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Reject failed.");
    } finally { setBusy(null); }
  }, [app.id, onChanged]);

  return { busy, result, err, onApprove, onReject };
}

function ApplicationRow({ app, onChanged, onApproved }) {
  const { busy, result, err, onApprove, onReject } = useRowActions({ app, onChanged, onApproved });
  const code = result?.issued_code || app.issued_code;
  const isPending = app.status === "pending";

  return (
    <article data-testid={`admin-app-${app.id}`} className="liquid-glass p-6 md:p-8 mb-5">
      <header className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <p className="font-display text-2xl mb-1" style={{ color: "var(--noor-bone)" }}>{app.full_name}</p>
          <p className="font-mono text-[11px]" style={{ color: "var(--text-secondary)", letterSpacing: "0.18em" }}>
            {app.email} · {app.city}
          </p>
        </div>
        <StatusPill status={app.status || "pending"} />
      </header>
      <p className="text-sm mb-5 leading-relaxed" style={{ color: "var(--text-secondary)" }}>{app.intro}</p>
      {code && (
        <p data-testid={`admin-code-${app.id}`} className="font-mono text-[12px] mb-4" style={{ color: "var(--accent-rose)", letterSpacing: "0.22em" }}>
          Issued · {code}
        </p>
      )}
      {err && (
        <p data-testid={`admin-error-${app.id}`} className="text-xs mb-3" style={{ color: "#E27676" }}>{err}</p>
      )}
      {isPending && (
        <ApplicationActions appId={app.id} busy={busy} onApprove={onApprove} onReject={onReject} />
      )}
      <p className="font-mono text-[9px] mt-4" style={{ color: "var(--text-secondary)", letterSpacing: "0.2em" }}>
        {app.created_at?.slice(0, 19).replace("T", " ") || ""}
      </p>
    </article>
  );
}

function EmailLogPanel({ entries }) {
  if (entries.length === 0) {
    return (
      <p className="font-mono text-[10px] opacity-60" style={{ color: "var(--text-secondary)", letterSpacing: "0.2em" }}>
        No emails dispatched yet.
      </p>
    );
  }
  return (
    <ul className="space-y-3" data-testid="admin-email-log">
      {entries.map((e) => (
        <li
          key={e.id}
          className="liquid-glass p-4 text-xs flex flex-col gap-1"
          style={{ color: "var(--text-secondary)" }}
        >
          <span className="font-mono text-[10px]" style={{ color: e.mock ? "var(--accent-rose)" : "#9BD6A2", letterSpacing: "0.22em" }}>
            {e.mock ? "MOCK" : "SENT"} · {e.status} · {e.created_at?.slice(11, 19)}
          </span>
          <span style={{ color: "var(--noor-bone)" }}>{e.subject}</span>
          <span className="font-mono text-[10px]">to {e.to_email}</span>
          <span className="font-mono text-[10px]" style={{ color: "var(--accent-rose)", letterSpacing: "0.18em" }}>
            {e.code}
          </span>
        </li>
      ))}
    </ul>
  );
}

function AdminApplicationsList({ loading, apps, filter, onRefresh, onApproved }) {
  if (loading) {
    return (
      <p className="font-mono text-[10px] opacity-60" style={{ color: "var(--text-secondary)", letterSpacing: "0.22em" }}>
        Loading…
      </p>
    );
  }
  if (apps.length === 0) {
    return (
      <p
        data-testid="admin-empty"
        className="font-mono text-[10px] opacity-60"
        style={{ color: "var(--text-secondary)", letterSpacing: "0.22em" }}
      >
        No {filter === "all" ? "" : filter} applications.
      </p>
    );
  }
  return apps.map((a) => (
    <ApplicationRow
      key={a.id}
      app={a}
      onChanged={onRefresh}
      onApproved={onApproved}
    />
  ));
}

function AdminHeader({ filter, count, approvedCount, onLogout }) {
  let subtitle;
  if (filter === "all") {
    subtitle = `${count} application${count === 1 ? "" : "s"}`;
  } else {
    subtitle = `${count} ${filter}`;
  }
  return (
    <header className="max-w-[1480px] mx-auto mb-12 flex items-start justify-between flex-wrap gap-4">
      <div>
        <p className="overline mb-3" style={{ color: "var(--accent-rose)" }}>House Admin</p>
        <h1 className="font-display text-4xl md:text-5xl" style={{ color: "var(--noor-bone)" }}>
          Applications &amp; allocations.
        </h1>
        <p className="text-sm font-light mt-2" style={{ color: "var(--text-secondary)" }}>
          {subtitle}{approvedCount ? ` · ${approvedCount} approved` : ""}
        </p>
      </div>
      <button
        data-testid="admin-logout"
        onClick={onLogout}
        className="btn-ghost"
        style={{ fontSize: "0.66rem", padding: "0.6rem 1.2rem" }}
      >
        Sign out
      </button>
    </header>
  );
}

function LastApprovalBanner({ approval, onDismiss }) {
  if (!approval) return null;
  return (
    <div
      data-testid="admin-last-approval"
      className="col-span-12 liquid-glass p-5 flex items-center justify-between flex-wrap gap-3"
      style={{ borderColor: "rgba(155,214,162,0.4)" }}
    >
      <div>
        <p className="font-mono text-[10px] uppercase mb-1" style={{ color: "#9BD6A2", letterSpacing: "0.28em" }}>
          Code minted · {approval.email_mock ? "Email logged (mock)" : "Email sent"}
        </p>
        <p style={{ color: "var(--noor-bone)" }}>
          {approval.full_name} <span style={{ color: "var(--text-secondary)" }}>· {approval.email}</span>
        </p>
      </div>
      <p
        data-testid="admin-last-approval-code"
        className="font-mono"
        style={{ color: "var(--accent-rose)", letterSpacing: "0.22em", fontSize: "1rem" }}
      >
        {approval.issued_code}
      </p>
      <button
        data-testid="admin-last-approval-dismiss"
        onClick={onDismiss}
        className="font-mono text-[10px] uppercase px-3 py-1"
        style={{ color: "var(--text-secondary)", letterSpacing: "0.28em" }}
      >
        Dismiss
      </button>
    </div>
  );
}

function filterButtonStyle(isActive) {
  return {
    letterSpacing: "0.28em",
    background: isActive ? "var(--accent-rose)" : "transparent",
    color: isActive ? "var(--noor-obsidian)" : "var(--text-secondary)",
    border: `1px solid ${isActive ? "var(--accent-rose)" : "var(--border-subtle)"}`,
  };
}

function FiltersBar({ filter, onChange, onRefresh }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
      <nav className="flex gap-2 flex-wrap" data-testid="admin-filters">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            data-testid={`admin-filter-${s}`}
            onClick={() => onChange(s)}
            className="font-mono text-[10px] uppercase px-3 py-2 rounded-sm transition-colors"
            style={filterButtonStyle(filter === s)}
          >
            {s}
          </button>
        ))}
      </nav>
      <button
        data-testid="admin-refresh"
        onClick={onRefresh}
        className="font-mono text-[10px] uppercase px-3 py-2 rounded-sm transition-colors"
        style={{
          letterSpacing: "0.28em",
          color: "var(--noor-rosegold)",
          border: "1px solid rgba(201,164,122,0.42)",
          background: "transparent",
        }}
      >
        ↻ Refresh
      </button>
    </div>
  );
}

// Hook owns dashboard data + refresh + auth-error escalation.
function useAdminData({ filter, onLogout }) {
  const [apps, setApps] = useState([]);
  const [log, setLog] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [appsData, logData] = await Promise.all([
        fetchApplications(filter),
        fetchEmailLog(),
      ]);
      setApps(appsData);
      setLog(logData);
    } catch (e) {
      if (e?.response?.status === 401) {
        onLogout();
        return;
      }
      setError("Could not load admin data.");
    } finally { setLoading(false); }
  }, [filter, onLogout]);

  // Initial fetch on mount + whenever filter changes via refresh's deps.
  useEffect(() => { refresh(); }, [refresh]);

  return { apps, log, error, loading, refresh };
}

function AdminDashboard({ onLogout }) {
  const [filter, setFilter] = useState("pending");
  // Most-recent approval kept so the issued code stays visible even after the
  // row filters out of the current view.
  const [lastApproval, setLastApproval] = useState(null);

  const { apps, log, error, loading, refresh } = useAdminData({ filter, onLogout });
  const dismissApproval = useCallback(() => setLastApproval(null), []);

  const counts = useMemo(() => {
    return apps.reduce((acc, a) => {
      const k = a.status || "pending";
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
  }, [apps]);

  return (
    <div className="min-h-screen pt-24 pb-24 px-6 md:px-10" data-testid="admin-dashboard">
      <AdminHeader filter={filter} count={apps.length} approvedCount={counts.approved} onLogout={onLogout} />

      <div className="max-w-[1480px] mx-auto grid grid-cols-12 gap-y-10 md:gap-10">
        <LastApprovalBanner approval={lastApproval} onDismiss={dismissApproval} />

        <section className="col-span-12 lg:col-span-8">
          <FiltersBar filter={filter} onChange={setFilter} onRefresh={refresh} />
          {error && (
            <p data-testid="admin-error" className="text-sm mb-4" style={{ color: "#E27676" }}>{error}</p>
          )}
          <AdminApplicationsList
            loading={loading}
            apps={apps}
            filter={filter}
            onRefresh={refresh}
            onApproved={setLastApproval}
          />
        </section>

        <aside className="col-span-12 lg:col-span-4">
          <p className="overline mb-4" style={{ color: "var(--accent-rose)" }}>Email Log</p>
          <p className="text-xs font-light mb-5" style={{ color: "var(--text-secondary)" }}>
            Latest 20 emails (mock or sent). When the live Resend key is set, these will say <span style={{ color: "#9BD6A2" }}>SENT</span> instead of <span style={{ color: "var(--accent-rose)" }}>MOCK</span>.
          </p>
          <EmailLogPanel entries={log} />
        </aside>
      </div>
    </div>
  );
}

export default function Admin() {
  // null = unknown / probing, false = unauthenticated, true = authed
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    let cancelled = false;
    adminSessionStatus()
      .then((ok) => { if (!cancelled) setAuthed(ok); })
      .catch(() => { if (!cancelled) setAuthed(false); });
    return () => { cancelled = true; };
  }, []);

  const handleAuthed = useCallback(() => setAuthed(true), []);
  const handleLogout = useCallback(async () => {
    // Drop back to the token gate regardless of server outcome — the cookie
    // (if any) will be invalid on the next request anyway. Silent on purpose:
    // no UI affordance to surface a logout-call failure to.
    try { await adminLogout(); } catch {
      // Server unreachable / cookie already expired → still log out client-side.
    }
    setAuthed(false);
  }, []);

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-probe">
        <p className="font-mono text-[10px] opacity-60" style={{ color: "var(--text-secondary)", letterSpacing: "0.28em" }}>
          Checking session…
        </p>
      </div>
    );
  }
  if (!authed) return <TokenGate onAuthed={handleAuthed} />;
  return <AdminDashboard onLogout={handleLogout} />;
}
